import { BrowserMultiFormatReader } from "@zxing/browser";
/**
 * BookOracle Scanner Service
 * Comprehensive scanner functionality matching legacy implementation
 */

// Scanner state tracking
let lastProcessedScan: string | null = null;
let lastProcessedTime = 0;

/**
 * Proactively request camera permissions with retry logic (legacy parity)
 */
export const requestCameraPermissionsEarly = async (): Promise<boolean> => {
  try {
    const cap = (window as any).Capacitor;
    if (!cap || !cap.Plugins?.BarcodeScanner) {
      return false;
    }
    const BarcodeScanner = cap.Plugins.BarcodeScanner;
    // Check if scanning is supported
    let supported = true;
    if (typeof BarcodeScanner.isSupported === "function") {
      try {
        const supportResult = await BarcodeScanner.isSupported();
        supported = supportResult?.supported ?? true;
      } catch (_e) {
        supported = true;
      }
    }
    if (!supported) return false;

    // Helper to normalize permission shapes
    const permGrantedFromResult = (res: any): boolean => {
      if (!res && res !== 0) return false;
      // Common shapes:
      // { granted: true }
      // { granted: 'granted' }
      // { camera: 'granted' }
      // { camera: { granted: true } }
      if (typeof res === 'boolean') return res;
      if (res.granted === true || res.granted === 'granted') return true;
      if (res.camera === 'granted') return true;
      if (res.camera && (res.camera.granted === true || res.camera.granted === 'granted')) return true;
      return false;
    };

    // Retry checkPermissions up to 3 times with 1s delay
    let permissionGranted = false;
    let retryCount = 0;
    const maxRetries = 3;
    while (retryCount < maxRetries && !permissionGranted) {
      try {
        const result = await BarcodeScanner.checkPermissions();
        if (permGrantedFromResult(result)) {
          permissionGranted = true;
          return true;
        }
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } catch (_error) {
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }
    if (!permissionGranted) {
      // Request permissions automatically
      const req = await BarcodeScanner.requestPermissions();
      const newGranted = permGrantedFromResult(req);
      return !!newGranted;
    }
    return permissionGranted;
  } catch (_error) {
    return false;
  }
};

export interface ScannerState {
  isScanning: boolean;
  isNativeAvailable: boolean;
  isBrowserAvailable: boolean;
  error: string | null;
  status: string;
}

export interface ScanResult {
  success: boolean;
  barcode?: string;
  error?: string;
}

/**
 * Check if native scanner is available (Capacitor) using CapacitorEnvContext if in React, fallback to window otherwise.
 */
import { getCapacitorEnv } from "@/utils/CapacitorEnvContext";
import { normalizeScanResult } from '@/utils/scanNormalizer';

export const checkNativeScanner = (): boolean => {
  // Try to use context if available, fallback to window for non-React usage
  let capEnv;
  try {
    capEnv = getCapacitorEnv();
  } catch {
    capEnv = null;
  }
  const cap = capEnv?.Capacitor || (window as any).Capacitor;
  const platform =
    capEnv?.platform ||
    (cap && typeof cap.getPlatform === "function"
      ? cap.getPlatform()
      : undefined);
  return !!(cap && platform !== "web" && cap.Plugins?.BarcodeScanner);
};

/**
 * Check if browser scanner is available
 */
export const checkBrowserScanner = async (): Promise<boolean> => {
  try {
    // Check if we can access camera
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    console.log("Browser scanner not available:", error);
    return false;
  }
};

/**
 * Start native scanner using Capacitor
 */
export const startNativeScanner = async (): Promise<ScanResult> => {
  // Suppress starting a native scan if one just completed very recently (prevents rapid re-open/duplicate)
  const last = (globalThis as any).__lastNativeScanComplete || 0;
  if (Date.now() - last < 800) {
    return { success: false, error: 'Suppressed duplicate native scan' };
  }

  if (!checkNativeScanner()) {
    return { success: false, error: "Native scanner not available" };
  }

  try {
    const Capacitor = (window as any).Capacitor;
    const BarcodeScanner = Capacitor.Plugins.BarcodeScanner;

    // Check if scanning is supported (optional, for robustness)
    let supported = true;
    if (typeof BarcodeScanner.isSupported === "function") {
      try {
        const supportResult = await BarcodeScanner.isSupported();
        supported = supportResult?.supported ?? true;
      } catch (_e) {
        supported = true; // fallback: assume supported if method fails
      }
    }
    if (!supported) {
      return {
        success: false,
        error: "Barcode scanning not supported on this device",
      };
    }

    // Legacy-style permission check/request with retry logic
    const permGrantedFromResult = (res: any): boolean => {
      if (!res && res !== 0) return false;
      if (typeof res === 'boolean') return res;
      if (res.granted === true || res.granted === 'granted') return true;
      if (res.camera === 'granted') return true;
      if (res.camera && (res.camera.granted === true || res.camera.granted === 'granted')) return true;
      return false;
    };

    let permissionGranted = false;
    let retryCount = 0;
    const maxRetries = 3;
    while (retryCount < maxRetries && !permissionGranted) {
      try {
        const result = await BarcodeScanner.checkPermissions();
        if (permGrantedFromResult(result)) {
          permissionGranted = true;
          break;
        }
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } catch (_error) {
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }
    if (!permissionGranted) {
      // Request permissions automatically
      const req = await BarcodeScanner.requestPermissions();
      permissionGranted = permGrantedFromResult(req);
      // After requesting, check again with a short delay (bridge sync)
      if (!permissionGranted) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const checkAfter = await BarcodeScanner.checkPermissions();
        permissionGranted = permGrantedFromResult(checkAfter);
      }
    }

    if (!permissionGranted) {
      // Now, and only now, suggest opening app settings
      if (
        Capacitor.Plugins.App &&
        typeof Capacitor.Plugins.App.openSettings === "function"
      ) {
        await Capacitor.Plugins.App.openSettings();
      }
      return {
        success: false,
        error:
          "Camera permission denied. Please enable camera access in your device settings and try again.",
      };
    }

    // Start scanning
    const rawResult: any = await BarcodeScanner.scan();
    const result = normalizeScanResult(rawResult);
    // Mark last native scan completion timestamp when successful to prevent immediate restarts
    if (result.success) {
      try {
        (globalThis as any).__lastNativeScanComplete = Date.now();
      } catch (_e) {
        // ignore
      }
    }
    // Push to global debug console if available
    if ((globalThis as any).__debugConsoleInstalled) {
      try {
        const { pushLog } = await import('@/utils/debugLog');
        pushLog({ rawResult, normalized: result, time: new Date().toISOString() });
      } catch (e) {
        console.debug('Failed to push scan result to DebugConsole', e);
      }
    }

    if (result.success && result.barcode) {
      return { success: true, barcode: result.barcode };
    } else {
      return { success: false, error: result.error || 'No barcode detected' };
    }
  } catch (error) {
    console.error("Native scanner error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Native scanner failed",
    };
  }
};

/**
 * Start browser scanner using ZXing (ES module import)
 */
export const startBrowserScanner = async (
  videoElement: HTMLVideoElement,
  onStatusUpdate?: (status: string) => void,
  onCancel?: () => boolean
): Promise<ScanResult> => {
  let stream: MediaStream | null = null;
  let codeReader: BrowserMultiFormatReader | null = null;
  let isCancelled = false;

  try {
    onStatusUpdate?.("Requesting camera access...");

    // Check for cancellation
    if (onCancel?.()) {
      return { success: false, error: "Scanner cancelled" };
    }

    // Get camera stream
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "environment",
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });

    // Set up video element
    videoElement.srcObject = stream;
    await videoElement.play();

    onStatusUpdate?.("Starting barcode detection...");

    // Create ZXing reader
    codeReader = new BrowserMultiFormatReader();

    // Start decoding
    return new Promise((resolve) => {
      const checkCancellation = () => {
        if (onCancel?.()) {
          isCancelled = true;
          if (stream) {
            stream.getTracks().forEach((track) => track.stop());
          }
          if (codeReader) {
            try {
              // No reset or stopContinuousDecode method in ES module; cleanup handled by stopping stream and nulling srcObject
            } catch (_e) {
              // reset() might not exist, try alternative cleanup
              console.log("[ScannerService] ZXing reader cleanup completed");
            }
          }
          resolve({ success: false, error: "Scanner cancelled" });
          return true;
        }
        return false;
      };

      // Check cancellation periodically
      const cancellationInterval = setInterval(() => {
        if (checkCancellation()) {
          clearInterval(cancellationInterval);
        }
      }, 100);

      if (codeReader) {
        codeReader.decodeFromVideoElement(
          videoElement,
          (result: any, error: any) => {
            if (isCancelled) return;

            if (result) {
              clearInterval(cancellationInterval);
              // Stop the stream
              if (stream) {
                stream.getTracks().forEach((track) => track.stop());
              }
              resolve({ success: true, barcode: result.text });
            } else if (error && error.name !== "NotFoundException") {
              console.warn("ZXing error:", error);
            }
          }
        );
      }

      // Set a timeout to stop scanning after 30 seconds
      setTimeout(() => {
        if (isCancelled) return;

        clearInterval(cancellationInterval);
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        if (codeReader) {
          try {
            // No reset or stopContinuousDecode method in ES module; cleanup handled by stopping stream and nulling srcObject
          } catch (_e) {
            // reset() might not exist, try alternative cleanup
            console.log("[ScannerService] ZXing reader cleanup completed");
          }
        }
        resolve({ success: false, error: "Scan timeout" });
      }, 30000);
    });
  } catch (error) {
    // Clean up on error
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    if (codeReader) {
      try {
        // No reset or stopContinuousDecode method in ES module; cleanup handled by stopping stream and nulling srcObject
      } catch (_e) {
        // reset() might not exist, try alternative cleanup
        console.log("[ScannerService] ZXing reader cleanup completed");
      }
    }

    console.error("Browser scanner error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Browser scanner failed",
    };
  }
};

/**
 * Stop scanner globally
 */
export const stopScanner = () => {
  // globalScannerActive = false; // This line is removed
  console.log("[ScannerService] Global scanner stopped");
};

/**
 * Handle successful scan with duplicate prevention
 */
export const handleSuccessfulScan = (barcode: string): boolean => {
  const now = Date.now();

  // Prevent duplicate processing of the same scan
  if (lastProcessedScan === barcode && now - lastProcessedTime < 10000) {
    console.log("Duplicate scan detected, ignoring:", barcode);
    return false;
  }

  // Update tracking
  lastProcessedScan = barcode;
  lastProcessedTime = now;

  console.log("[ScannerService] Successful scan:", barcode);
  return true;
};

/**
 * Extract ISBN from barcode
 */
export const extractISBNFromBarcode = (barcode: string): string => {
  // Remove any non-digit characters except X (for ISBN-10)
  const cleanBarcode = barcode.replace(/[^0-9X]/gi, "");

  // Check if it's a valid ISBN
  if (validateISBN(cleanBarcode)) {
    return cleanBarcode;
  }

  // If not a valid ISBN, return the original barcode
  return barcode;
};

/**
 * Validate ISBN format
 */
export const validateISBN = (isbn: string): boolean => {
  // Remove hyphens and spaces
  const cleanISBN = isbn.replace(/[-\s]/g, "");

  // Check if it's a valid ISBN-10 or ISBN-13
  if (cleanISBN.length === 10) {
    return /^\d{9}[\dX]$/.test(cleanISBN);
  } else if (cleanISBN.length === 13) {
    return /^\d{13}$/.test(cleanISBN);
  }

  return false;
};

/**
 * Format ISBN for display
 */
export const formatISBN = (isbn: string): string => {
  const cleanISBN = isbn.replace(/[-\s]/g, "");

  if (cleanISBN.length === 10) {
    return `${cleanISBN.slice(0, 1)}-${cleanISBN.slice(1, 4)}-${cleanISBN.slice(
      4,
      9
    )}-${cleanISBN.slice(9)}`;
  } else if (cleanISBN.length === 13) {
    return `${cleanISBN.slice(0, 3)}-${cleanISBN.slice(3, 4)}-${cleanISBN.slice(
      4,
      8
    )}-${cleanISBN.slice(8, 12)}-${cleanISBN.slice(12)}`;
  }

  return isbn;
};

/**
 * Get scanner availability status
 */
export const getScannerAvailability = async (): Promise<{
  native: boolean;
  browser: boolean;
}> => {
  const native = checkNativeScanner();
  const browser = await checkBrowserScanner();

  return { native, browser };
};
